import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatVnDateTimeShort } from '../../utils/vnDateTime';
import {
  approveMerchantKyc,
  fetchMerchantKycDetail,
  fetchMerchantKycList,
  rejectMerchantKyc,
  type MerchantKycDetail,
  type MerchantKycRow,
  type MerchantKycStatus,
} from '../../services/merchantKycService';
import { EmptyState } from '../../design-system/EmptyState';
import { MerchantKycDetailDrawer } from './MerchantKycDetailDrawer';
import { AppotaPayReferActivationFlow } from '../../components/appotapay/AppotaPayReferActivationFlow';

const PAGE_SIZE = 20;

function statusTag(status: string) {
  switch (status) {
    case 'submitted':
      return <Tag color="processing">Chờ duyệt</Tag>;
    case 'approved':
      return <Tag color="success">Đã duyệt</Tag>;
    case 'rejected':
      return <Tag color="error">Từ chối</Tag>;
    case 'completed':
      return <Tag color="success">Hoàn tất</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

function paymentTag(status?: string) {
  if (status === 'paid') return <Tag color="green">Đã TT</Tag>;
  if (status === 'skipped') return <Tag>Miễn phí</Tag>;
  return <Tag color="orange">Chưa TT</Tag>;
}

export default function MerchantKycListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MerchantKycRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<MerchantKycStatus | 'ALL'>(
    'submitted'
  );
  const [rejectRow, setRejectRow] = useState<MerchantKycRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveRow, setApproveRow] = useState<MerchantKycRow | null>(null);
  const [approvedNext, setApprovedNext] = useState<{
    merchantId: number;
    storeName?: string | null;
  } | null>(null);

  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<MerchantKycDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMerchantKycList({
        page,
        limit: PAGE_SIZE,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      if (res.success) {
        setRows(res.data ?? []);
        setTotal(res.pagination?.total ?? 0);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(ax.response?.data?.message || ax.message || 'Lỗi tải dữ liệu');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (userId: number) => {
    setDetailUserId(userId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await fetchMerchantKycDetail(userId);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setDetailError(res.message || 'Không tải được chi tiết hồ sơ');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setDetailError(
        ax.response?.data?.message || 'Không tải được chi tiết hồ sơ'
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailUserId(null);
    setDetail(null);
    setDetailError(null);
  };

  const handleApproveConfirm = async () => {
    if (!approveRow) return;
    setActionLoading(approveRow.userId);
    try {
      const res = await approveMerchantKyc(approveRow.userId);
      const merchantId = res.merchantId ?? approveRow.restaurantId ?? null;
      setApproveRow(null);
      if (merchantId) {
        setApprovedNext({
          merchantId,
          storeName: approveRow.storeName,
        });
      } else {
        message.success('Đã duyệt KYC');
      }
      await load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Duyệt thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectRow) return;
    setActionLoading(rejectRow.userId);
    try {
      await rejectMerchantKyc(
        rejectRow.userId,
        rejectReason.trim() || undefined
      );
      message.success('Đã từ chối hồ sơ');
      setRejectRow(null);
      setRejectReason('');
      await load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Từ chối thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const columns: ColumnsType<MerchantKycRow> = [
    { title: 'User ID', dataIndex: 'userId', width: 90 },
    {
      title: 'Quán',
      dataIndex: 'storeName',
      ellipsis: true,
      render: (name, row) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => openDetail(row.userId)}
        >
          {name || '—'}
        </Button>
      ),
    },
    {
      title: 'Chủ quán',
      dataIndex: 'merchantName',
      width: 140,
      ellipsis: true,
    },
    { title: 'SĐT', dataIndex: 'merchantPhone', width: 120 },
    {
      title: 'Gói',
      key: 'package',
      width: 160,
      render: (_, r) => r.packageName || '—',
    },
    {
      title: 'KYC',
      dataIndex: 'status',
      width: 110,
      render: s => statusTag(String(s)),
    },
    {
      title: 'Thanh toán',
      dataIndex: 'packagePaymentStatus',
      width: 100,
      render: s => paymentTag(s),
    },
    {
      title: 'Lên sàn',
      dataIndex: 'isListed',
      width: 90,
      render: v => (v ? <Tag color="success">Có</Tag> : <Tag>Chưa</Tag>),
    },
    {
      title: 'AppotaPay',
      key: 'appotapay',
      width: 160,
      render: (_, row) => {
        if (row.status !== 'approved' && row.status !== 'completed') {
          return '—';
        }
        if (!row.restaurantId) return <Tag>Chưa có quán</Tag>;
        return (
          <Link to={`/appotapay/merchants/${row.restaurantId}?link=1`}>
            Nhập credentials →
          </Link>
        );
      },
    },
    {
      title: 'Gửi lúc',
      dataIndex: 'submittedAt',
      width: 150,
      render: v => (v ? formatVnDateTimeShort(v) : '—'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: 260,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openDetail(row.userId)}>
            Chi tiết
          </Button>
          <Button
            type="primary"
            size="small"
            loading={actionLoading === row.userId}
            disabled={row.status !== 'submitted'}
            onClick={() => {
              setApproveRow(row);
            }}
          >
            Duyệt
          </Button>
          <Button
            danger
            size="small"
            disabled={row.status === 'rejected' || row.status === 'approved'}
            onClick={() => {
              setRejectRow(row);
              setRejectReason('');
            }}
          >
            Từ chối
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="Duyệt KYC Merchant (app)"
        style={{ borderRadius: 16 }}
        extra={
          <Select
            value={statusFilter}
            style={{ width: 180 }}
            onChange={v => {
              setPage(1);
              setStatusFilter(v);
            }}
            options={[
              { value: 'submitted', label: 'Chờ duyệt' },
              { value: 'approved', label: 'Đã duyệt' },
              { value: 'rejected', label: 'Từ chối' },
              { value: 'ALL', label: 'Tất cả' },
            ]}
          />
        }
      >
        {error && (
          <Alert type="error" message={error} style={{ marginBottom: 16 }} />
        )}
        <Table
          rowKey="userId"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1260 }}
          onRow={row => ({
            onClick: e => {
              const target = e.target as HTMLElement;
              if (
                target.closest('button') ||
                target.closest('a') ||
                target.closest('.ant-btn')
              ) {
                return;
              }
              openDetail(row.userId);
            },
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <EmptyState
                description="Không có hồ sơ KYC"
                showActions={false}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </Card>

      <MerchantKycDetailDrawer
        open={detailUserId != null}
        loading={detailLoading}
        error={detailError}
        detail={detail}
        onClose={closeDetail}
      />

      <Modal
        title={`Duyệt KYC — ${approveRow?.storeName || ''}`}
        open={!!approveRow}
        onCancel={() => setApproveRow(null)}
        onOk={handleApproveConfirm}
        confirmLoading={actionLoading != null}
        okText="Duyệt KYC (bước 1)"
      >
        <p style={{ color: '#666', marginBottom: 12 }}>
          Xác nhận duyệt hồ sơ KYC trên AnGi. Bước này <strong>không</strong> tự
          tạo merchant trên portal AppotaPay.
        </p>
        <AppotaPayReferActivationFlow status="PENDING_ACTIVATION" compact />
      </Modal>

      <Modal
        title={`KYC đã duyệt — ${approvedNext?.storeName || ''}`}
        open={!!approvedNext}
        onCancel={() => setApprovedNext(null)}
        footer={[
          <Button key="close" onClick={() => setApprovedNext(null)}>
            Đóng
          </Button>,
          <Button
            key="list"
            onClick={() => {
              setApprovedNext(null);
              navigate('/appotapay/merchants');
            }}
          >
            Danh sách AppotaPay
          </Button>,
          <Button
            key="credentials"
            type="primary"
            onClick={() => {
              const id = approvedNext?.merchantId;
              setApprovedNext(null);
              if (id) navigate(`/appotapay/merchants/${id}?link=1`);
            }}
          >
            Nhập credentials (bước 3)
          </Button>,
        ]}
      >
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="Bước 1 hoàn tất — vận hành tạo REFER trên portal AppotaPay rồi nhập credentials"
        />
        <AppotaPayReferActivationFlow
          status="PENDING_ACTIVATION"
          merchantId={approvedNext?.merchantId}
        />
        <p style={{ color: '#666', marginTop: 16, marginBottom: 0 }}>
          Vận hành cần tạo Sub Merchant <strong>REFER</strong> trên portal
          AppotaPay trước. Khi AppotaPay gửi credentials, bấm{' '}
          <strong>Nhập credentials</strong>.
        </p>
      </Modal>

      <Modal
        title="Từ chối hồ sơ KYC"
        open={!!rejectRow}
        onCancel={() => setRejectRow(null)}
        onOk={handleReject}
        confirmLoading={actionLoading != null}
        okText="Từ chối"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={4}
          placeholder="Lý do từ chối (tuỳ chọn)"
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
